import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TagChip } from "@/lib/tags";
import { ArticleBody } from "@/lib/articleBody";
import { Newspaper, ArrowRight } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function ForecastArticle() {
  const [article, setArticle] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/articles/latest")
      .then((r) => setArticle(r.data))
      .catch(() => setArticle(null))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || !article) return null;


  return (
    <section id="forecast-article" data-testid="forecast-article" className="relative py-14">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <div className="mb-6 flex items-center gap-2">
          <Newspaper className="h-4 w-4 text-[#1f63c9]" />
          <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#1f63c9]">
            Meteorologist's Discussion
          </div>
        </div>

        <article className="relative overflow-hidden rounded-sm border border-white/10 bg-gradient-to-br from-black via-[#0a111f] to-[#0b1424] p-8 sm:p-12">
          <div className="stripe-blue absolute inset-0 opacity-[0.05]" />

          <div className="relative">
            {article.image_url && (
              <div className="-mx-8 -mt-8 mb-7 sm:-mx-12 sm:-mt-12" data-testid="forecast-article-image">
                <div className="relative h-56 w-full overflow-hidden sm:h-72">
                  <img src={article.image_url} alt={article.title} loading="lazy"
                       className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b1424] via-transparent to-transparent" />
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <div className="font-oswald text-[10px] uppercase tracking-[0.4em] text-[#ffd60a]">
                {formatDate(article.published_at)}
              </div>
              {(article.tags || []).length > 0 && (
                <>
                  <span className="text-white/20">·</span>
                  <div className="flex flex-wrap gap-1.5">
                    {article.tags.map((t) => (
                      <TagChip key={t} tag={t} size="xs" />
                    ))}
                  </div>
                </>
              )}
            </div>

            <h2 className="mt-3 font-oswald text-3xl font-bold uppercase leading-tight tracking-wider text-white sm:text-4xl lg:text-5xl">
              {article.title}
            </h2>

            {article.subtitle && (
              <div className="mt-3 text-base text-gray-300 sm:text-lg">
                {article.subtitle}
              </div>
            )}

            <div className="mt-5 h-[2px] w-16 bg-[#1f63c9]" />

            <div className="mt-6 space-y-4 text-sm leading-relaxed text-gray-200 sm:text-base">
              <ArticleBody body={article.body} />
            </div>

            {article.author && (
              <div className="mt-8 flex items-center gap-3 border-t border-white/10 pt-5">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1f63c9] font-oswald text-xs font-bold uppercase tracking-widest text-white">
                  {article.author.split(/\s+/).map((w) => w[0]).slice(0, 2).join("")}
                </div>
                <div className="flex-1">
                  <div className="font-oswald text-sm font-bold uppercase tracking-wider text-white">
                    {article.author}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
                    Chief Meteorologist · Georgia Weather Network
                  </div>
                </div>
                <Link
                  to="/forecast"
                  data-testid="forecast-view-archive"
                  className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 font-oswald text-[10px] font-bold uppercase tracking-widest text-white hover:border-[#1f63c9] hover:text-[#1f63c9]"
                >
                  View Archive <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </div>
        </article>
      </div>
    </section>
  );
}
